import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/system";
import { useState, useEffect } from "react";
import { useFormAccordo } from "../ContextAccordo";
import { DataGrid } from "@mui/x-data-grid";
import { NumericFormat } from "react-number-format";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
    },
  },
};

const UtentiModale = ({
  open,
  setOpen,
  agenziaSelezionata,
  setAgenziaSelezionata,
}) => {
  const { formState, dispatch } = useFormAccordo();
  const theme = useTheme();

  // State to manage the selected agency and its users
  const [selectedAgenzia, setSelectedAgenzia] = useState(agenziaSelezionata);
  const [utenti, setUtenti] = useState([]);
  const [searchTerm, setSearchTerm] = useState(""); // State for the search term
  const [filteredUtenti, setFilteredUtenti] = useState([]); // State for filtered users
  const [feeTutti, setFeeTutti] = useState("0");
  const [loading, setLoading] = useState(true);

  const [all, setAll] = useState({
    feeAgente: 0,
    feeAgenzia: 0,
    tipoFeeAgente: 1,
    tipoFeeAgenzia: 1,
    abilitato: 0,
    visibile: 0,
    acquistabile: 0,
  });

  const {
    feeAgente,
    feeAgenzia,
    tipoFeeAgente,
    tipoFeeAgenzia,
    abilitato,
    visibile,
    acquistabile,
  } = all;

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Update the users when the selected agency changes
  useEffect(() => {
    if (selectedAgenzia) {
      setLoading(true);
      const agenzia = formState.agenzie.find(
        (a) => a.nomeAzienda === selectedAgenzia
      );
      setTimeout(() => {
        setUtenti(agenzia?.utenti || []);
        setFilteredUtenti(agenzia?.utenti || []);
        setLoading(false); // Set loading to false after rows are loaded
      }, 500); // Simulate a delay for loading
    }
  }, [selectedAgenzia, formState.agenzie]);

  useEffect(() => {
    setSelectedAgenzia(agenziaSelezionata);
  }, [agenziaSelezionata]);

  //FIlter users based on the search term
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredUtenti(utenti); // Show all users if search term is empty
    } else {
      setFilteredUtenti(
        utenti.filter((utente) =>
          utente.onominativo.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }
  }, [searchTerm, utenti]);

  // Track changes to utenti
  const handleUtenteChange = (updatedUtente, field, value) => {
    setUtenti((prevUtenti) =>
      prevUtenti.map((utente) =>
        utente.id === updatedUtente.id ? { ...utente, [field]: value } : utente
      )
    );
  };

  // Save changes to the formState
  const handleSave = () => {
    // Dispatch the updated utenti for the selected agency
    setOpen(false); // Close the modal after saving
    dispatch({
      type: "set_utenti",
      id: formState.agenzie.find((a) => a.nomeAzienda === selectedAgenzia)?.id,
      payload: { utenti },
    });
    setUtenti([]);
    setFilteredUtenti([]);
    setSelectedAgenzia("");
    setSearchTerm("");
    setFeeTutti("0");
    setAgenziaSelezionata("");
    setAll({
      feeAgente: 0,
      feeAgenzia: 0,
      tipoFeeAgente: 1,
      tipoFeeAgenzia: 1,
      abilitato: 0,
      visibile: 0,
      acquistabile: 0,
    });
  };

  const handleClose = () => {
    // Reset the utenti state to the original state of the selected agency
    setOpen(false); // Close the modal without dispatching changes
    setUtenti([]);
    setFilteredUtenti([]);
    setSelectedAgenzia("");
    setSearchTerm("");
    setFeeTutti("0");
    setAll({
      feeAgente: 0,
      feeAgenzia: 0,
      tipoFeeAgente: 1,
      tipoFeeAgenzia: 1,
      abilitato: 0,
      visibile: 0,
      acquistabile: 0,
    });
    setAgenziaSelezionata("");
  };

  // Columns for the DataGrid
  const columns = [
    { field: "nominativo", headerName: "Nominativo", minWidth: 250 },
    {
      field: "feeAgente",
      headerName: "Fee Agente",
      minWidth: 200,
      renderCell: (params) => (
        <>
          <FormControl size="small" margin="none">
            <Select
              id={`tipoFeeAgente/${params.id}`}
              name={`tipoFeeAgente/${params.id}`}
              value={params.row.tipoFeeAgente}
              MenuProps={MenuProps}
            >
              {[
                { el: "%", id: 1 },
                { el: "€", id: 2 },
              ].map((el) => (
                <MenuItem
                  key={el.id}
                  value={el.id}
                  onClick={() =>
                    handleUtenteChange(params.row, "tipoFeeAgente", el.id)
                  }
                >
                  {el.el}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <NumericFormat
            customInput={TextField}
            thousandSeparator="."
            decimalSeparator=","
            decimalScale={2}
            sx={{ width: 100 }}
            id={`feeAgente/${params.id}`}
            name={`feeAgente/${params.id}`}
            value={params.row.feeAgente}
            onValueChange={(e) =>
              handleUtenteChange(params.row, "feeAgente", e.value)
            }
          />
        </>
      ),
    },
    {
      field: "feeAgenzia",
      headerName: "Fee Agenzia",
      minWidth: 200,
      renderCell: (params) => (
        <>
          <FormControl size="small" margin="none">
            <Select
              id={`tipoFeeAgenzia/${params.id}`}
              name={`tipoFeeAgenzia/${params.id}`}
              value={params.row.tipoFeeAgenzia}
              MenuProps={MenuProps}
            >
              {[
                { el: "%", id: 1 },
                { el: "€", id: 2 },
              ].map((el) => (
                <MenuItem
                  key={el.id}
                  value={el.id}
                  onClick={() =>
                    handleUtenteChange(params.row, "tipoFeeAgenzia", el.id)
                  }
                >
                  {el.el}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <NumericFormat
            customInput={TextField}
            thousandSeparator="."
            decimalSeparator=","
            decimalScale={2}
            sx={{ width: 100 }}
            id={`feeAgenzia/${params.id}`}
            name={`feeAgenzia/${params.id}`}
            value={params.row.feeAgenzia}
            onValueChange={(e) =>
              handleUtenteChange(params.row, "feeAgenzia", e.value)
            }
          />
        </>
      ),
    },
    {
      field: "abilitato",
      headerName: "Abilitato",
      minWidth: 100,
      renderCell: (params) => (
        <Switch
          id={`abilitato/${params.id}`}
          name={`abilitato/${params.id}`}
          checked={params.row.abilitato === 1}
          onChange={(e) =>
            handleUtenteChange(
              params.row,
              "abilitato",
              e.target.checked ? 1 : 0
            )
          }
          sx={{
            "&.MuiSwitch-root .MuiSwitch-switchBase": {
              color: "#ccc",
            },
            "&.MuiSwitch-root .Mui-checked": {
              color: "#407ad6",
            },
          }}
        />
      ),
    },
    {
      field: "visibile",
      headerName: "Visibile",
      minWidth: 100,
      renderCell: (params) => (
        <Switch
          id={`visibile/${params.id}`}
          name={`visibile/${params.id}`}
          checked={params.row.visibile === 1}
          onChange={(e) =>
            handleUtenteChange(params.row, "visibile", e.target.checked ? 1 : 0)
          }
          sx={{
            "&.MuiSwitch-root .MuiSwitch-switchBase": {
              color: "#ccc",
            },
            "&.MuiSwitch-root .Mui-checked": {
              color: "#407ad6",
            },
          }}
        />
      ),
    },
    {
      field: "acquistabile",
      headerName: "Acquistabile",
      minWidth: 100,
      renderCell: (params) => (
        <Switch
          id={`acquistabile/${params.id}`}
          name={`acquistabile/${params.id}`}
          checked={params.row.acquistabile === 1}
          onChange={(e) =>
            handleUtenteChange(
              params.row,
              "acquistabile",
              e.target.checked ? 1 : 0
            )
          }
          sx={{
            "&.MuiSwitch-root .MuiSwitch-switchBase": {
              color: "#ccc",
            },
            "&.MuiSwitch-root .Mui-checked": {
              color: "#407ad6",
            },
          }}
        />
      ),
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "1050px", // Set your width here
            backgroundColor:
              theme.palette.mode === "dark" ? "#1B2635" : "#F7F9FC",
          },
        },
      }}
    >
      <DialogTitle>Utenti di: {selectedAgenzia}</DialogTitle>
      <DialogContent>
        <TextField
          disabled={utenti.length === 0 ? true : false}
          size="small"
          margin="none"
          id="ricerca_nominativo"
          name="ricerca_nominativo"
          label="Ricerca Nominativo"
          placeholder="Ricerca Nominativo"
          required
          fullWidth
          sx={{ mt: "16px" }}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)} // Update search term
        />
        <Box
          sx={{
            my: "24px",
            display: "grid",
            gridTemplateColumns: isMobile ? "repeat(1, 1fr)" : "repeat(5, 1fr)",
            gap: 4,
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            flexDirection="column"
            gap={2}
          >
            <Typography>Fee Agente</Typography>
            <Box display="flex">
              <Select
                id="tipoFeeAgente/Generale"
                name="tipoFeeAgente/Generale"
                value={tipoFeeAgente}
                MenuProps={MenuProps}
                disabled={utenti.length === 0 ? true : false}
              >
                {[
                  { el: "%", id: 1 },
                  { el: "€", id: 2 },
                ].map((el) => (
                  <MenuItem
                    key={el.id}
                    value={el.id}
                    onClick={() =>
                      setAll((prev) => ({
                        ...prev,
                        tipoFeeAgente: el.id,
                      }))
                    }
                  >
                    {el.el}
                  </MenuItem>
                ))}
              </Select>
              <NumericFormat
                id="feeAgente/Generale"
                name="feeAgente/Generale"
                defaultValue="0"
                width={"50%"}
                customInput={TextField} // Ensures proper input behavior in edit mode
                value={feeAgente}
                onValueChange={(e) => {
                  setAll((prev) => ({
                    ...prev,
                    feeAgente: e.floatValue,
                  }));
                }}
                thousandSeparator="."
                decimalSeparator=","
                decimalScale={2}
                disabled={utenti.length === 0 ? true : false}
              />
            </Box>
          </Box>
          <Box
            display="flex"
            alignItems="center"
            flexDirection="column"
            gap={2}
          >
            <Typography>Fee Agenzia</Typography>
            <Box display="flex">
              <Select
                id="tipoFeeAgenzia/Generale"
                name="tipoFeeAgenzia/Generale"
                disabled={utenti.length === 0 ? true : false}
                value={tipoFeeAgenzia}
                MenuProps={MenuProps}
              >
                {[
                  { el: "%", id: 1 },
                  { el: "€", id: 2 },
                ].map((el) => (
                  <MenuItem
                    key={el.id}
                    value={el.id}
                    onClick={() =>
                      setAll((prev) => ({
                        ...prev,
                        tipoFeeAgenzia: el.id,
                      }))
                    }
                  >
                    {el.el}
                  </MenuItem>
                ))}
              </Select>
              <NumericFormat
                id="feeAgenzia/Generale"
                name="feeAgenzia/Generale"
                defaultValue={0}
                width={"50%"}
                customInput={TextField}
                value={feeAgenzia ?? 0}
                onValueChange={(e) => {
                  setAll((prev) => ({
                    ...prev,
                    feeAgenzia: e.floatValue ?? 0,
                  }));
                }}
                thousandSeparator="."
                decimalSeparator=","
                decimalScale={2}
                disabled={utenti.length === 0 ? true : false}
              />
            </Box>
          </Box>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={2}
          >
            <Typography>Abilitato</Typography>
            <Switch
              id="abilitato/Generale"
              name="abilitato/Generale"
              value={abilitato === 1 ? true : false}
              onChange={() => {
                setAll((prev) => ({
                  ...prev,
                  abilitato: prev.abilitato === 1 ? 0 : 1,
                }));
              }}
              sx={{
                "&.MuiSwitch-root .MuiSwitch-switchBase": {
                  color: "#ccc",
                },
                "&.MuiSwitch-root .Mui-checked": {
                  color: "#407ad6",
                },
              }}
              disabled={utenti.length === 0 ? true : false}
            />
          </Box>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={2}
          >
            <Typography>Visibile</Typography>
            <Switch
              id="visibile/Generale"
              name="visibile/Generale"
              value={visibile === 1 ? true : false}
              onChange={() => {
                setAll((prev) => ({
                  ...prev,
                  visibile: prev.visibile === 1 ? 0 : 1,
                }));
              }}
              sx={{
                "&.MuiSwitch-root .MuiSwitch-switchBase": {
                  color: "#ccc",
                },
                "&.MuiSwitch-root .Mui-checked": {
                  color: "#407ad6",
                },
              }}
              disabled={utenti.length === 0 ? true : false}
            />
          </Box>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={2}
          >
            <Typography>Acquistabile</Typography>
            <Switch
              id="acquistabile/Generale"
              name="acquistabile/Generale"
              value={acquistabile === 1 ? true : false}
              onChange={() => {
                setAll((prev) => ({
                  ...prev,
                  acquistabile: prev.acquistabile === 1 ? 0 : 1,
                }));
              }}
              sx={{
                "&.MuiSwitch-root .MuiSwitch-switchBase": {
                  color: "#ccc",
                },
                "&.MuiSwitch-root .Mui-checked": {
                  color: "#407ad6",
                },
              }}
              disabled={utenti.length === 0 ? true : false}
            />
          </Box>
        </Box>
        <Box
          display="flex"
          justifyContent="end"
          alignItems="center"
          width="100%"
          my={4}
        >
          <Button
            variant="contained"
            disabled={utenti.length === 0 ? true : false}
            onClick={() => {
              setUtenti((prev) =>
                prev.map((utente) => ({
                  ...utente,
                  abilitato: abilitato,
                  visibile: visibile,
                  acquistabile: acquistabile,
                  tipoFeeAgente: tipoFeeAgente,
                  feeAgente: feeAgente,
                  tipoFeeAgenzia: tipoFeeAgenzia,
                  feeAgenzia: feeAgenzia,
                }))
              );
            }}
          >
            Imposta
          </Button>
        </Box>

        {/* DataGrid to display users */}
        <Paper style={{ height: "400px", width: "100%" }}>
          <DataGrid
            sx={{
              backgroundColor: theme.palette.mode === "dark" && "#233044",
            }}
            rows={filteredUtenti}
            columns={columns}
            getRowId={(row) => row.id}
            autoPageSize
            pageSize={10}
            disableSelectionOnClick
            components={{
              NoRowsOverlay: () => (
                <Stack
                  height="100%"
                  alignItems="center"
                  justifyContent="center"
                >
                  No rows in DataGrid
                </Stack>
              ),
            }}
            loading={loading}
            slotProps={{
              loadingOverlay: {
                variant: "linear",
                noRowsVariant: "linear",
              },
            }}
          />
        </Paper>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Chiudi
        </Button>
        <Button onClick={handleSave}>Salva</Button>
      </DialogActions>
    </Dialog>
  );
};

export default UtentiModale;
